import React, { useEffect, useRef, useState } from "react";
import { Check, Search, UserPlus2 } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { ClientFactory } from "@/main/common/ClientFactory";
import type { AnswerRequestAskState, AnswerRequestSuggestion } from "@/main/common/AnswerRequestTypes";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import SimpleDialog, { openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import { getDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import type { Localizer } from "@/main/react/Types";
import logger from "@/lib/logger";

/**
 * "Ask someone to answer" (SV-130, question loop Part B, design
 * `designs/01-now/2026-09-10-question-loop-follow-and-ask-design.md` §B2).
 *
 * <p>The question's author, on their own open question, picks up to three people: the server's
 * suggestions first (people who wrote about the topic, plus followers who pass the same gates), then
 * anyone by username through the existing person search. Each person is asked ONCE and gets one
 * notification; the row then reads "Asked" and stays that way (A1: a dismissal is private and the asker
 * never learns of it, so the chip never comes back off).
 *
 * <p><b>Every refusal is quiet (A4).</b> The server answers a 403 or 409 with no body on purpose, so this
 * dialog never explains one: the pressed row simply returns to rest. A network failure is the only thing
 * that earns a toast, and it is the generic one.
 *
 * <p><b>D10: zero suggestions still opens the dialog</b>, with the search alone; the suggestion list
 * collapses to one line. <b>The cap replaces the search</b>, not the list, so the three "Asked" chips stay
 * visible as the record of what was sent.
 *
 * <p>The person ROW is the control — a real button carrying the avatar, the name and the description —
 * because the title and the helper already say what pressing one does, and a verb on every row would
 * repeat the title three to nine times.
 */

type Person = AnswerRequestSuggestion & { suggested: boolean };
type RowState = "askable" | "asking" | "asked";

function AskToAnswerDialogInner(props: { discussionPublicId: string; localizer: Localizer }) {
    const { discussionPublicId, localizer } = props;
    const [state, setState] = useState<AnswerRequestAskState | null>(null);
    const [loading, setLoading] = useState(true);
    const [asked, setAsked] = useState<Set<string>>(new Set());
    const [asking, setAsking] = useState<string | null>(null);
    const [query, setQuery] = useState("");
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<Person[]>([]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        ClientFactory.getAnswerRequestState(discussionPublicId)
            .then(({ data }) => {
                if (!mounted) return;
                setState(data);
                setAsked(new Set(data?.asked ?? []));
            })
            .catch((error) => {
                if (!mounted) return;
                // A 403 here means the viewer is not this question's author any more (or it resolved
                // under them): nothing to offer, and nothing to explain (A4).
                logger.warn("[AskToAnswerDialog] state fetch failed", error);
                setState({ suggestions: [], asked: [], remaining: 0, max: 3 });
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, [discussionPublicId]);

    // Username search, server-side (the person search the people vertical already uses), debounced
    // and token-guarded so a slow earlier query cannot overwrite a later one's results.
    const latestSearchToken = useRef(0);
    useEffect(() => {
        const trimmed = query.trim();
        if (trimmed.length === 0) {
            latestSearchToken.current += 1;
            setSearching(false);
            setSearchResults([]);
            return;
        }
        const token = ++latestSearchToken.current;
        setSearching(true);
        const timer = window.setTimeout(() => {
            ClientFactory.searchUsersWithAttemptContext(trimmed)
                .then(({ data }) => {
                    if (token !== latestSearchToken.current) return;
                    setSearchResults((data ?? [])
                        .filter((user) => Boolean(user.username))
                        .map((user) => ({
                            username: user.username as string,
                            description: user.description ?? null,
                            profileImageUrl: user.profileImageUrl ?? null,
                            suggested: false,
                        })));
                })
                .catch((error) => {
                    if (token !== latestSearchToken.current) return;
                    logger.warn("[AskToAnswerDialog] search failed", error);
                })
                .finally(() => {
                    if (token === latestSearchToken.current) setSearching(false);
                });
        }, 250);
        return () => window.clearTimeout(timer);
    }, [query]);

    const max = state?.max ?? 3;
    const remaining = Math.max(0, max - asked.size);
    const limitReached = !loading && remaining === 0;

    const ask = async (person: Person) => {
        if (asking || asked.has(person.username) || limitReached) {
            return;
        }
        setAsking(person.username);
        try {
            await ClientFactory.sendAnswerRequest(discussionPublicId, {
                username: person.username,
                suggested: person.suggested,
            });
            setAsked((previous) => new Set(previous).add(person.username));
        } catch (error) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status === 403 || status === 409) {
                // Quiet by design (A4): the server will not say why, and neither may this dialog.
                logger.info("[AskToAnswerDialog] ask refused quietly", { status });
            } else {
                logger.error("[AskToAnswerDialog] ask failed", error);
                toast.error(localizer("shared.action.failed"));
            }
        } finally {
            setAsking(null);
        }
    };

    const rowStateFor = (person: Person): RowState => (
        asked.has(person.username) ? "asked" : asking === person.username ? "asking" : "askable"
    );

    function renderRow(person: Person) {
        const rowState = rowStateFor(person);
        const displayUsername = getDisplayUsername(person.username, localizer);
        const disabled = rowState !== "askable" || limitReached;
        return (
            <li key={person.username}>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => void ask(person)}
                    data-playwright-id="ask-to-answer-person"
                    data-username={person.username}
                    data-row-state={rowState}
                    className="flex w-full min-w-0 items-center gap-3 rounded-xl border border-border p-2 text-left standard-hover disabled:cursor-default disabled:opacity-100 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                    <ProfileImage localizer={localizer}
                        imageUrl={person.profileImageUrl}
                        username={person.username}
                        mode="user"
                        hoverable={false}
                        className="size-10 shrink-0"
                    />
                    <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium" translate="no">{displayUsername}</span>
                        {person.description ? (
                            <span className="block truncate text-sm text-muted-foreground">{person.description}</span>
                        ) : null}
                    </span>
                    {rowState === "asked" ? (
                        <span
                            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                            data-playwright-id="ask-to-answer-asked-chip"
                        >
                            <Check className="size-3" aria-hidden="true" />
                            {localizer("discussion.ask_dialog.sent")}
                        </span>
                    ) : rowState === "asking" ? (
                        <Spinner className="size-4 shrink-0 text-neutral" />
                    ) : !limitReached ? (
                        <UserPlus2 className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    ) : null}
                </button>
            </li>
        );
    }

    const suggestions: Person[] = (state?.suggestions ?? []).map((suggestion) => ({ ...suggestion, suggested: true }));

    return (
        <div className="flex w-full flex-col gap-4" data-playwright-id="ask-to-answer-dialog" data-remaining={remaining}>
            <p className="text-sm text-muted-foreground">{localizer("discussion.ask_dialog.helper")}</p>

            {loading ? (
                <div className="flex justify-center py-6">
                    <Spinner className="size-5 text-neutral" />
                </div>
            ) : (
                <section aria-labelledby="ask-to-answer-suggestions-heading" className="flex flex-col gap-2">
                    <h3 id="ask-to-answer-suggestions-heading" className="text-sm font-medium">
                        {localizer("discussion.ask_dialog.suggestions_heading")}
                    </h3>
                    {suggestions.length === 0 ? (
                        <p className="text-sm text-muted-foreground" data-playwright-id="ask-to-answer-suggestions-empty">
                            {localizer("discussion.ask_dialog.suggestions_empty")}
                        </p>
                    ) : (
                        <ul className="flex flex-col gap-2" data-playwright-id="ask-to-answer-suggestions">
                            {suggestions.map(renderRow)}
                        </ul>
                    )}
                </section>
            )}

            {limitReached ? (
                <p className="text-sm text-muted-foreground" data-playwright-id="ask-to-answer-limit-reached">
                    {localizer("discussion.ask_dialog.limit_reached")}
                </p>
            ) : (
                <div className="flex flex-col gap-2">
                    <div className="relative w-full">
                        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral" aria-hidden="true" />
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={localizer("discussion.ask_dialog.search_placeholder")}
                            aria-label={localizer("discussion.ask_dialog.search_placeholder")}
                            className="w-full rounded-lg border border-border bg-input/70 py-3 pl-10 pr-4 text-sm [@media(pointer:coarse)]:text-base text-text-main outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                            data-playwright-id="ask-to-answer-search"
                        />
                        {searching ? (
                            <Spinner className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-neutral" />
                        ) : null}
                    </div>
                    {searchResults.length > 0 ? (
                        <ul className="flex flex-col gap-2" data-playwright-id="ask-to-answer-search-results">
                            {searchResults
                                .filter((person) => !suggestions.some((suggestion) => suggestion.username === person.username))
                                .map(renderRow)}
                        </ul>
                    ) : null}
                </div>
            )}
        </div>
    );
}

export function showAskToAnswerDialog(discussionPublicId: string, localizer: Localizer) {
    return openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={localizer("discussion.ask_dialog.title")}
            width="lg"
            dataPlaywrightId="ask-to-answer"
        >
            <AskToAnswerDialogInner discussionPublicId={discussionPublicId} localizer={localizer} />
        </SimpleDialog>
    ));
}

export default showAskToAnswerDialog;
